import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import portalAxios from '../../api/portalAxios';
import FileDropZoneField from '../ui/fileDropZoneField/fileDropZoneField.component';
import {
  getPortalTicketEndpoint,
  PORTAL_ATTACHMENTS_ENDPOINT,
  PORTAL_EQUIPMENT_ENDPOINT,
  PORTAL_TICKETS_ENDPOINT,
} from '../../consts';
import {
  PortalAttachment,
  PortalEquipment,
  PortalJob,
  PortalJobTask,
} from '../../types/portal';
import { extractPortalError } from './portalError';
import styles from './portal.module.css';

const MAX_ATTACHMENTS = 10;

/** One machine on the report, with the files that show what is wrong with it. */
type MachineRow = {
  /** The machine itself. */
  equipmentId: number;
  /** The JobTask it already is, when revising a report that was sent. */
  taskId?: number;
  label: string;
  attachments: PortalAttachment[];
};

type Props = {
  /** Editing an existing report. */
  job?: PortalJob;
  /** Raising a complaint about this job - its equipment is inherited. */
  complaintForJob?: PortalJob;
  onDone: (createdId?: number) => void;
  onCancel?: () => void;
};

const machineLabel = (machine: PortalEquipment) =>
  [machine.producer_name, machine.model_name, machine.serial].filter(Boolean).join(' ');

const taskLabel = (task: PortalJobTask, fallback: string) =>
  task.equipment
    ? [task.equipment.producer_name, task.equipment.model_name, task.equipment.serial]
        .filter(Boolean)
        .join(' ')
    : fallback;

/** The machines of a job being edited, or of the job a complaint is about.
 *  A complaint inherits the machines but starts with no files: the files on
 *  the earlier job document that visit, not this new report. */
const machineRowsFromJob = (
  source: PortalJob | undefined,
  keepFiles: boolean
): MachineRow[] =>
  (source?.tasks ?? [])
    .filter((task) => task.equipment)
    .map((task) => ({
      equipmentId: task.equipment!.id,
      taskId: keepFiles ? task.id : undefined,
      label: taskLabel(task, ''),
      attachments: keepFiles ? task.attachments ?? [] : [],
    }));

export const PortalTicketForm = ({ job, complaintForJob, onDone, onCancel }: Props) => {
  const { t } = useTranslation();

  const isEdit = Boolean(job);
  const isComplaint = Boolean(complaintForJob);

  const [equipment, setEquipment] = useState<PortalEquipment[] | null>(null);
  const [description, setDescription] = useState(job?.description ?? '');
  // A machine the report already covers cannot be added or dropped here, so
  // an edit and a complaint start from the job's own tasks; only a fresh
  // report builds the list from the picker.
  const [machines, setMachines] = useState<MachineRow[]>(() =>
    machineRowsFromJob(job ?? complaintForJob, Boolean(job))
  );
  const [error, setError] = useState<string | null>(null);
  // Which machine's drop zone is mid-upload, so only that one is disabled.
  const [uploadingFor, setUploadingFor] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const needsEquipmentPicker = !isEdit && !isComplaint;

  const load = useCallback(async () => {
    if (!needsEquipmentPicker) {
      setEquipment([]);
      return;
    }

    try {
      const response = await portalAxios.get(PORTAL_EQUIPMENT_ENDPOINT);
      setEquipment(response.data);
    } catch {
      setError(t('We could not load your equipment.'));
      setEquipment([]);
    }
  }, [needsEquipmentPicker, t]);

  useEffect(() => {
    load();
  }, [load]);

  const totalAttachments = machines.reduce(
    (count, machine) => count + machine.attachments.length,
    0
  );

  const handleUpload = async (equipmentId: number, file: File) => {
    setUploadingFor(equipmentId);
    setError(null);
    try {
      const form = new FormData();
      form.append('attachment', file);
      const response = await portalAxios.post(PORTAL_ATTACHMENTS_ENDPOINT, form);
      setMachines((current) =>
        current.map((machine) =>
          machine.equipmentId === equipmentId
            ? { ...machine, attachments: [...machine.attachments, response.data] }
            : machine
        )
      );
    } catch (uploadError) {
      setError(extractPortalError(uploadError, t('We could not upload the file.')));
    } finally {
      setUploadingFor(null);
    }
  };

  const handleRemove = (equipmentId: number, attachmentId: number) => {
    setMachines((current) =>
      current.map((machine) =>
        machine.equipmentId === equipmentId
          ? {
              ...machine,
              attachments: machine.attachments.filter((row) => row.id !== attachmentId),
            }
          : machine
      )
    );
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      if (isEdit && job) {
        // Which machines a job concerns is settled when it is raised, so an
        // edit only revises the description and each machine's files - the
        // full list is resent per machine, and whatever is missing from it is
        // dropped server-side.
        await portalAxios.patch(getPortalTicketEndpoint(job.id), {
          description,
          tasks: machines.map((machine) => ({
            id: machine.taskId,
            attachment_ids: machine.attachments.map((attachment) => attachment.id),
          })),
        });
        onDone();
        return;
      }

      const response = await portalAxios.post(PORTAL_TICKETS_ENDPOINT, {
        description,
        tasks: machines.map((machine) => ({
          equipment_id: machine.equipmentId,
          attachment_ids: machine.attachments.map((attachment) => attachment.id),
        })),
        ...(isComplaint ? { complaint_for: complaintForJob?.id } : {}),
      });
      onDone(response.data.id);
    } catch (submitError) {
      setError(extractPortalError(submitError, t('We could not send your report.')));
    } finally {
      setSaving(false);
    }
  };

  if (equipment === null) {
    return (
      <Box className={styles.centered}>
        <CircularProgress />
      </Box>
    );
  }

  const canSubmit = isEdit || isComplaint || machines.length > 0;

  return (
    <Box className={styles.form} sx={{ pt: 1 }}>
      {isComplaint ? (
        <Alert severity="info">
          {t('This complaint concerns job {{number}} and the equipment it covered.', {
            number: complaintForJob?.number,
          })}
        </Alert>
      ) : null}

      {needsEquipmentPicker ? (
        <FormControl fullWidth size="small">
          <InputLabel>{t('Equipment')}</InputLabel>
          <Select
            multiple
            label={t('Equipment')}
            value={machines.map((machine) => machine.equipmentId)}
            onChange={(event: SelectChangeEvent<number[]>) => {
              const selected = event.target.value as number[];
              // Rebuilt rather than replaced, so files already attached to a
              // machine survive the client picking another one.
              setMachines((current) =>
                selected.map(
                  (id) =>
                    current.find((machine) => machine.equipmentId === id) ?? {
                      equipmentId: id,
                      label: machineLabel(
                        equipment.find((row) => row.id === id) as PortalEquipment
                      ),
                      attachments: [],
                    }
                )
              );
            }}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as number[]).map((id) => {
                  const machine = equipment.find((row) => row.id === id);
                  return (
                    <Chip
                      key={id}
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={machine ? machineLabel(machine) : id}
                    />
                  );
                })}
              </Box>
            )}
          >
            {equipment.map((machine) => (
              <MenuItem key={machine.id} value={machine.id}>
                {machineLabel(machine)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : null}

      <TextField
        size="small"
        label={t('What is wrong?')}
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        multiline
        minRows={3}
        fullWidth
      />

      {machines.length ? (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('Attachments')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('Add the photos for each machine separately.')}
          </Typography>
          {machines.map((machine) => (
            <Box key={machine.equipmentId} sx={{ mt: 1.5 }}>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
                {machine.label || t('Not specified')}
              </Typography>
              <FileDropZoneField
                onFileSelected={(file) => handleUpload(machine.equipmentId, file)}
                uploading={uploadingFor === machine.equipmentId}
                disabled={
                  uploadingFor !== null || totalAttachments >= MAX_ATTACHMENTS
                }
              />
              {machine.attachments.length ? (
                <Box className={styles.attachmentRow}>
                  {machine.attachments.map((attachment) => (
                    <Chip
                      key={attachment.id}
                      size="small"
                      label={attachment.attachment.split('/').pop()}
                      onDelete={() => handleRemove(machine.equipmentId, attachment.id)}
                    />
                  ))}
                </Box>
              ) : null}
            </Box>
          ))}
        </Box>
      ) : null}

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        {onCancel ? (
          <Button variant="outlined" onClick={onCancel} disabled={saving}>
            {t('Cancel')}
          </Button>
        ) : null}
        <Button variant="contained" onClick={handleSubmit} disabled={saving || !canSubmit}>
          {saving ? t('Saving...') : isEdit ? t('Save') : t('Send report')}
        </Button>
      </Box>
    </Box>
  );
};
